import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button/Button';
import { FormInput } from 'components/FormInput/FormInput';
import FormRow from 'components/FormRow/FormRow';
import InfoBox from 'components/InfoBox/InfoBox';
import { useAppSettings } from 'context/appsettingsContext';
import { useAuth } from 'context/useAuth';
import { ClientInterface } from 'functions/hooks/useGetUsers';
import { trimInputValue } from 'functions/trimInputValue';
import { validateInput } from 'functions/validationUtils';
import { FormLayout } from 'layouts/FormLayout/FormLayout';
import { FormErrors } from 'types/signUpForm';
import { errorAsync, successAsync } from 'functions/notification';
import axios from 'axios';

export interface ClientDetailType {
  newDriver?: false;
  Email: string;
  FirstName: string;
  IdNumber: string;
  IdNumberIsSouthAfrican: boolean;
  LastName: string;
  PhoneCode?: string;
  PhoneNumber: string;
  acceptedTermsAndConditionsAndPolicy: boolean;
  acceptedToAllowInformationToBeUsedToContact: boolean;
}

export function handleOnChange(
  e: ChangeEvent<HTMLInputElement>,
  setClientDetails:
    | Dispatch<SetStateAction<ClientDetailType>>
    | Dispatch<SetStateAction<ClientInterface>>,
  setClientDetailsError: Dispatch<SetStateAction<any>>,
  isRSARegion: boolean = false,
  isUSARegion: boolean = false,
) {
  const { id, value, type, checked } = e.target;
  const inputValue = trimInputValue(id, value);

  if (id === 'PhoneNumber') {
    if (isRSARegion) {
      // RSA phone numbers - numbers only
      //if (REGEX.Numbers.test(inputValue) || !inputValue) {
        setClientDetails((prevState: any) => ({
          ...prevState,
          [id]: inputValue,
        }));
      //}
    } else if (isUSARegion) {
      // US phone numbers - allow formatting characters
      // Format: (XXX) XXX-XXXX
      //const cleaned = inputValue.replace(/\D/g, ''); // Remove non-digits
      //if (cleaned.length <= 10) {
        //let formatted = cleaned;
        //if (cleaned.length > 0) formatted = '(' + formatted;
        //if (cleaned.length > 3) formatted = formatted.slice(0,4) + ') ' + formatted.slice(4);
        //if (cleaned.length > 6) formatted = formatted.slice(0,9) + '-' + formatted.slice(9);
        
        setClientDetails((prevState: any) => ({
          ...prevState,
          [id]: inputValue,
        }));
      //}
    } else /*isUKRegion*/ {
      // UK phone numbers - allow formatting characters
      // Format: (XXX) XXX-XXXX
      //const cleaned = inputValue.replace(/\D/g, ''); // Remove non-digits
      //if (cleaned.length <= 10) {
        //let formatted = cleaned;
        //if (cleaned.length > 0) formatted = '(' + formatted;
        //if (cleaned.length > 3) formatted = formatted.slice(0,4) + ') ' + formatted.slice(4);
        //if (cleaned.length > 6) formatted = formatted.slice(0,9) + '-' + formatted.slice(9);
        
        setClientDetails((prevState: any) => ({
          ...prevState,
          [id]: inputValue,
        }));
      //}
    }
  } else {
    setClientDetails((prevState: any) => ({
      ...prevState,
      [id]: type === 'checkbox' ? checked : inputValue,
    }));
  }

  if (id === 'IdNumberIsSouthAfrican') {
    setClientDetails((prevState: any) => ({
      ...prevState,
      IdNumber: '',
    }));

    setClientDetailsError((prevState: any) => ({
      ...prevState,
      IdNumber: [],
    }));
  }
}

export default function AddClientForm() {
  const navigate = useNavigate();
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Determine region based on firebase project ID
  const isRSARegion = settings.firebaseProjectId.toLowerCase().endsWith('rsa') || settings.firebaseProjectId.toLowerCase().endsWith('rsa-stg');
  const isUSARegion = settings.firebaseProjectId.toLowerCase().endsWith('usa') || settings.firebaseProjectId.toLowerCase().endsWith('usa-stg');
  
  const [clientDetails, setClientDetails] = useState<ClientDetailType>({
    Email: '',
    FirstName: '',
    IdNumber: '',
    IdNumberIsSouthAfrican: isRSARegion,
    LastName: '',
    PhoneCode: isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44',
    PhoneNumber: '',
    acceptedTermsAndConditionsAndPolicy: true,
    acceptedToAllowInformationToBeUsedToContact: true,
  });

  const [clientDetailsError, setClientDetailsError] = useState<FormErrors>({
    Email: [],
    FirstName: [],
    IdNumber: [],
    LastName: [],
    PhoneNumber: [],
  });

  function handleInputValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setClientDetailsError((prevState) => ({
      ...prevState,
      [id]: validateInput(id, value, isRSARegion, isUSARegion, clientDetails.IdNumberIsSouthAfrican),
    }));
  }

  async function handleSubmit() {
    setIsSubmitting(true);

    try {
      
      const response = await axios.post<any, any>(settings.addUserUrl, {
        firstName: clientDetails.FirstName,
        lastName: clientDetails.LastName,
        email: clientDetails.Email,
        emailVerified: true,
        phoneCode: isRSARegion ? '27' : isUSARegion ? '1' : /*isUKRegion*/ '44',
        phoneNumber: clientDetails.PhoneNumber,
        idNumber: clientDetails.IdNumber,
        idNumberIsSouthAfrican: clientDetails.IdNumberIsSouthAfrican
      }, { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } });

      successAsync(response?.data?.message || "Submitted");

      const userId = response?.data?.userId || response?.userId;

      navigate(`/agent/onboarding/client/${userId}/add-driver`);

    } catch (error: any) {
      errorAsync(`Failed to create : ${error?.response?.data?.message || error?.message || error}`, error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <FormLayout>
      <FormLayout.Header>
        <FormLayout.HeaderBack onClose={handleGoBack} />
        <p>Account holder</p>
      </FormLayout.Header>
      <FormLayout.Main>
        <InfoBox
          title='Who is responsible for the account?'
          helperText="Kindly gather the customer's personal details, including their
              full name, contact information, and address. Ensure accurate
              record-keeping for future correspondence and installation
              scheduling."
        />
        <form>
          <FormRow>
            <FormInput
              id='FirstName'
              label='Name'
              value={clientDetails.FirstName}
              onChange={(e) => {
                handleOnChange(e, setClientDetails, setClientDetailsError);
                handleInputValidation(e);
              }}
              errorMessage={clientDetailsError?.FirstName}
              onBlur={handleInputValidation}
            />
          </FormRow>
          <FormRow>
            <FormInput
              id='LastName'
              label='Surname'
              value={clientDetails.LastName}
              onChange={(e) => {
                handleOnChange(e, setClientDetails, setClientDetailsError);
                handleInputValidation(e);
              }}
              errorMessage={clientDetailsError?.LastName}
              onBlur={handleInputValidation}
            />
          </FormRow>
          <FormRow>
            <FormInput
              id='Email'
              label='Email address'
              value={clientDetails.Email}
              onChange={(e) => {
                handleOnChange(e, setClientDetails, setClientDetailsError);
                handleInputValidation(e);
              }}
              errorMessage={clientDetailsError?.Email}
              onBlur={handleInputValidation}
            />
          </FormRow>
          <FormRow>
            <FormInput
              type='tel'
              id='PhoneNumber'
              value={clientDetails.PhoneNumber}
              label='Cellphone number'
              onChange={(e) => {
                handleOnChange(e, setClientDetails, setClientDetailsError);
                handleInputValidation(e);
              }}
              errorMessage={clientDetailsError?.PhoneNumber}
              onBlur={handleInputValidation}
              countryCode={isRSARegion ? '+27' : isUSARegion ? '+1' : /*isUKRegion*/ '+44'}
            />
          </FormRow>
          
          <FormRow>
            <FormInput
                type='text'
                id='IdNumber'
                label={'ID Number'}
                value={clientDetails.IdNumber}
                onChange={(e) => {
                    handleOnChange(e, setClientDetails, setClientDetailsError);
                    handleInputValidation(e);
                }}
                errorMessage={clientDetailsError?.IdNumber}
            />
          </FormRow>
        </form>
      </FormLayout.Main>
      <FormLayout.Footer>
        <Button
          label={isSubmitting ? 'Submitting' : 'Next'}
          fullWidth
          onClick={handleSubmit}
          disabled={
            !clientDetails.FirstName ||
            !clientDetails.LastName ||
            !clientDetails.PhoneNumber ||
            !clientDetails.Email ||
            !clientDetails.IdNumber ||
            // Add checking for clientDetailsError
            clientDetailsError.Email.length > 0 ||
            clientDetailsError.FirstName.length > 0 ||
            clientDetailsError.LastName.length > 0 ||
            clientDetailsError.PhoneNumber.length > 0 ||
            clientDetailsError.IdNumber.length > 0 ||

            isSubmitting
          }
        />
      </FormLayout.Footer>
    </FormLayout>
  );
}